import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiShield } from 'react-icons/fi';
import { Helmet } from 'react-helmet';
import { jsPDF } from 'jspdf';

const DefensivePublication = () => {
  const publicationDate = "December 14th, 2025";
  const companyName = "Snap-Chart AI";
  
  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    let yPosition = 20;
    
    // Header with branding
    doc.setFillColor(147, 51, 234); // Purple
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Snap Chart AI - Prior Art Documentation', margin, 22);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Defensive Publication - Plutus AI Risk Officer', margin, 30);
    
    yPosition = 50;
    doc.setTextColor(0, 0, 0);
    
    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Defensive Publication - Plutus AI Risk Officer', margin, yPosition);
    yPosition += 10;
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const subtitle = doc.splitTextToSize('AI-Based Institutional Trading Risk Officer with Behavioral Discipline Intelligence', maxWidth);
    doc.text(subtitle, margin, yPosition);
    yPosition += subtitle.length * 6 + 8;
    
    // Metadata
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Applicant / Publisher:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Snap-Chart AI (https://snap-chart.ai/)', margin + 40, yPosition);
    yPosition += 6;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Product Name:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Plutus (Institutional Risk Officer)', margin + 30, yPosition);
    yPosition += 6;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Publication Date:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(publicationDate, margin + 35, yPosition);
    yPosition += 6;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Jurisdiction:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Global (Defensive Prior Art)', margin + 25, yPosition);
    yPosition += 12;
    
    // Abstract
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Abstract', margin, yPosition);
    yPosition += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const abstractText = doc.splitTextToSize(
      'This publication describes a system and method for providing AI-assisted institutional trading risk oversight, designed to evaluate trading decisions before execution, incorporating trader-specific risk profiles, behavioral patterns, and contextual market inputs. The system acts as a digital institutional risk officer, focused on capital protection, discipline enforcement, and decision gating, rather than signal generation or trade prediction.',
      maxWidth
    );
    doc.text(abstractText, margin, yPosition);
    yPosition += abstractText.length * 4 + 8;
    
    // Field of the Invention
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Field of the Invention', margin, yPosition);
    yPosition += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const fields = [
      '• Financial technology (FinTech)',
      '• Artificial intelligence applied to trading risk management',
      '• Behavioral finance and trader discipline systems',
      '• Pre-trade decision governance for discretionary and systematic traders'
    ];
    fields.forEach(field => {
      doc.text(field, margin, yPosition);
      yPosition += 5;
    });
    yPosition += 5;
    
    // Check if we need a new page
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }
    
    // Background
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Background', margin, yPosition);
    yPosition += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const backgroundText = doc.splitTextToSize(
      'Most trading platforms focus on charting, indicators, signal generation, and post-trade analytics. However, professional trading institutions rely heavily on risk officers, discipline rules, and behavioral oversight to prevent over-trading, emotional decisions, risk limit violations, and capital drawdowns caused by human behavior. There exists a need for a software-based institutional risk oversight layer that operates before trade execution, is personalized per trader, and adapts to behavioral patterns over time.',
      maxWidth
    );
    doc.text(backgroundText, margin, yPosition);
    yPosition += backgroundText.length * 4 + 10;
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('© 2025 Quantum AI Fintech Corporation. All rights reserved.', margin, 285);
    doc.text('This document constitutes defensive prior art publication.', margin, 290);
    
    doc.save('Snap-Chart-AI-Defensive-Publication-Plutus.pdf');
  };

  return (
    <>
      <Helmet>
        <title>Defensive Publication - Plutus AI Risk Officer | Snap Chart AI</title>
        <meta name="description" content="Defensive prior art publication for Plutus, an AI-based institutional trading risk officer with behavioral discipline intelligence. Published December 14th, 2025." />
        <meta name="keywords" content="defensive publication, prior art, trading AI, risk officer, Plutus, Snap Chart AI, fintech patent, trading discipline" />
        <link rel="canonical" href="https://snap-chart.ai/legal/defensive-publication-plutus" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white">
        {/* Header */}
        <div className="border-b border-white/10 bg-gray-900/80 backdrop-blur-xl sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
              to="/legal/defensive-publications" 
              className="flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors"
            >
              <FiArrowLeft className="text-lg" />
              <span>Back to Defensive Publications</span>
            </Link>
            <button
              onClick={generatePDF}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 rounded-lg text-cyan-400 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-cyan-500/50 transition-all"
            >
              <FiDownload className="text-lg" />
              <span>Download PDF</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          {/* Title Section */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                <FiShield className="text-2xl text-cyan-400" />
              </div>
              <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 text-sm font-medium rounded-full border border-cyan-500/30">
                Defensive Prior Art
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Defensive Publication - Plutus
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              AI-Based Institutional Trading Risk Officer with Behavioral Discipline Intelligence
            </p>
          </div>

          {/* Metadata Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
            <div className="p-4 bg-gray-800/50 rounded-xl border border-white/10">
              <p className="text-sm text-gray-500 mb-1">Applicant / Publisher</p>
              <p className="text-white font-medium">{companyName}</p>
              <a href="https://snap-chart.ai/" className="text-cyan-400 text-sm hover:underline">https://snap-chart.ai/</a>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-xl border border-white/10">
              <p className="text-sm text-gray-500 mb-1">Product Name</p>
              <p className="text-white font-medium">Plutus</p>
              <p className="text-gray-400 text-sm">Institutional Risk Officer</p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-xl border border-white/10">
              <p className="text-sm text-gray-500 mb-1">Publication Date</p>
              <p className="text-white font-medium">{publicationDate}</p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-xl border border-white/10">
              <p className="text-sm text-gray-500 mb-1">Jurisdiction</p>
              <p className="text-white font-medium">Global (Defensive Prior Art)</p>
            </div>
          </div>

          {/* Main Content */}
          <article className="prose prose-invert prose-lg max-w-none [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:bg-gradient-to-r [&_h2]:from-purple-400 [&_h2]:to-cyan-400 [&_h2]:bg-clip-text [&_h2]:text-transparent [&_h2]:mb-6 [&_h2]:mt-12 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-white [&_h3]:mb-4 [&_h3]:mt-8 [&_p]:text-gray-300 [&_p]:leading-relaxed [&_p]:mb-4 [&_ul]:space-y-4 [&_li]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-6">
            
            <h2>Abstract</h2>
            <p>
              This publication describes a system and method for providing AI-assisted institutional trading risk oversight, 
              designed to evaluate trading decisions before execution, incorporating trader-specific risk profiles, 
              behavioral patterns, and contextual market inputs.
            </p>
            <p>
              The system acts as a digital institutional risk officer, focused on capital protection, discipline enforcement, 
              and decision gating, rather than signal generation or trade prediction.
            </p>

            <h2>Field of the Invention</h2>
            <p>The present disclosure relates to:</p>
            <ul>
              <li>Financial technology (FinTech)</li>
              <li>Artificial intelligence applied to trading risk management</li>
              <li>Behavioral finance and trader discipline systems</li>
              <li>Pre-trade decision governance for discretionary and systematic traders</li>
            </ul>

            <h2>Background</h2>
            <p>Most trading platforms focus on:</p>
            <ul>
              <li>Charting</li>
              <li>Indicators</li>
              <li>Signal generation</li>
              <li>Post-trade analytics</li>
            </ul>
            <p>
              However, professional trading institutions rely heavily on risk officers, discipline rules, 
              and behavioral oversight to prevent:
            </p>
            <ul>
              <li>Over-trading</li>
              <li>Emotional decisions</li>
              <li>Risk limit violations</li>
              <li>Capital drawdowns caused by human behavior</li>
            </ul>
            <p>
              There exists a need for a software-based institutional risk oversight layer that operates before 
              trade execution, is personalized per trader, and adapts to behavioral patterns over time.
            </p>

            <h2>Summary of the System (High-Level)</h2>
            <p>The system comprises an AI-driven risk officer that:</p>

            <h3>1) Receives trader input</h3>
            <ul>
              <li>Trade ideas (manual or verbal)</li>
              <li>Chart images or contextual references</li>
              <li>Natural language questions</li>
            </ul>

            <h3>2) Incorporates a trader-specific risk profile</h3>
            <ul>
              <li>Account size</li>
              <li>Risk per trade</li>
              <li>Maximum daily / weekly loss</li>
              <li>Market permissions</li>
              <li>Trading style constraints</li>
            </ul>

            <h3>3) Evaluates decisions through a risk governance layer</h3>
            <ul>
              <li>Capital exposure checks</li>
              <li>Risk-reward validation</li>
              <li>Frequency and over-trading detection</li>
              <li>Rule-based gating (approve / wait / reject)</li>
            </ul>

            <h3>4) Integrates behavioral context</h3>
            <ul>
              <li>Historical trading behavior</li>
              <li>Discipline patterns</li>
              <li>Emotional or impulsive indicators inferred from interaction patterns</li>
              <li>Session-level continuity and memory</li>
            </ul>

            <h3>5) Produces human-like risk feedback</h3>
            <ul>
              <li>Conversational responses (text and/or voice)</li>
              <li>Natural language explanations</li>
              <li>Clear risk decisions without providing trade signals</li>
            </ul>

            <h3>6) Operates across multiple asset classes</h3>
            <ul>
              <li>Crypto</li>
              <li>Forex</li>
              <li>Indices</li>
              <li>Commodities</li>
              <li>Equities</li>
            </ul>

            <h2>Behavioral Intelligence Layer (Conceptual)</h2>
            <p>The system may derive a Trader Behavior Fingerprint, representing aggregated characteristics such as:</p>
            <ul>
              <li>Discipline consistency</li>
              <li>Impulsivity vs patience</li>
              <li>Risk adherence over time</li>
              <li>Reaction to wins/losses</li>
              <li>Frequency patterns</li>
            </ul>
            <p>This fingerprint influences:</p>
            <ul>
              <li>Tone of AI responses</li>
              <li>Strictness of risk enforcement</li>
              <li>Decision pacing recommendations</li>
            </ul>
            <p className="text-yellow-400/80 italic">
              Note: Specific algorithms, weighting models, scoring formulas, and thresholds are implementation-specific and intentionally undisclosed.
            </p>

            <h2>Decision Authority Model</h2>
            <p>The AI system:</p>
            <ul>
              <li>Does not provide trade signals</li>
              <li>Does not predict price movements</li>
              <li>Does not guarantee outcomes</li>
            </ul>
            <p>Instead, it:</p>
            <ul>
              <li>Acts as a risk gatekeeper</li>
              <li>Enforces predefined risk boundaries</li>
              <li>Has authority to recommend WAIT or NO-TRADE</li>
              <li>Prioritizes capital preservation and discipline</li>
            </ul>

            <h2>Human–Machine Interface</h2>
            <p>The system may include:</p>
            <ul>
              <li>Conversational chat interface</li>
              <li>Voice interaction</li>
              <li>Real-time feedback during decision analysis</li>
              <li>Dashboard visualization of risk and discipline metrics</li>
            </ul>

            <h2>Distinguishing Characteristics</h2>
            <p>Key differentiators of this system include:</p>
            <ul>
              <li>Pre-execution risk governance (not post-trade analytics)</li>
              <li>Behavioral discipline intelligence</li>
              <li>Trader-specific adaptive risk logic</li>
              <li>Institutional risk officer paradigm applied to individuals and teams</li>
              <li>Separation between decision approval and market prediction</li>
            </ul>

            <h2>Intended Use Cases</h2>
            <ul>
              <li>Individual discretionary traders</li>
              <li>Prop trading firms</li>
              <li>Trading teams</li>
              <li>Fund managers</li>
              <li>Educational trading environments</li>
              <li>Institutional risk compliance layers</li>
            </ul>

            <h2>Disclaimer</h2>
            <p>This publication intentionally omits:</p>
            <ul>
              <li>Model architectures</li>
              <li>Training methods</li>
              <li>Prompt structures</li>
              <li>Scoring algorithms</li>
              <li>Data schemas</li>
              <li>Threshold values</li>
              <li>Implementation details</li>
            </ul>
            <p className="text-yellow-400/80 italic">
              These elements remain protected as trade secrets.
            </p>

            <h2>Purpose of Publication</h2>
            <div className="p-6 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 rounded-xl border border-purple-500/30 mt-6">
              <p className="text-lg text-white font-medium mb-0">
                This document is published as defensive prior art to prevent the patenting of the described concepts by third parties.
              </p>
            </div>
          </article>

          {/* Footer */}
          <div className="mt-16 pt-8 border-t border-white/10">
            <p className="text-gray-500 text-sm text-center">
              © 2025 Quantum AI Fintech Corporation. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default DefensivePublication;
